import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import PropTypes from 'prop-types';

import { QUESTION_TYPES } from '@api/instant_survey';
import { Button, Checkbox, Fieldset, Layout, OverlayPopover, Pill, Radio } from '@utilities';

import { FORMATS } from '../../utilities/constants';
import styles from './_index.module.scss';

const AnswersFormat = ({ question, setQuestion }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [propertiesToApply, setPropertiesToApply] = useState({ ...question.properties });

  const pillRef = useRef();
  const popoverRef = useRef(); // required to allow inputs inside the popover to be focused

  useEffect(() => {
    if (isPopoverOpen) {
      setPropertiesToApply({ ...question.properties });
    }
  }, [isPopoverOpen]);

  const handleApply = useCallback(() => {
    setQuestion((prev) => ({
      ...prev,
      properties: {
        ...prev.properties,
        ...propertiesToApply,
        ...(propertiesToApply[FORMATS.PERCENT.value] && {
          maxNumber: 100,
          ...(question.type === QUESTION_TYPES.CONTSUM.value && { maxTotal: 100 }),
        }),
      },
    }));
    setIsPopoverOpen(false);
  }, [propertiesToApply, setQuestion]);

  const isApplyDisabled = isEqual(question.properties, propertiesToApply);
  const pillValue = question.properties?.forceNumeric
    ? FORMATS.NUMBER.name
    : question.properties?.forcePercent
      ? FORMATS.PERCENT.name
      : FORMATS.CURRENCY.name;

  return (
    <>
      <Layout.Flex className={styles['answers-format']} gap="x-small">
        <span ref={popoverRef}>Format</span>
        <Pill
          buttonRef={pillRef}
          isModified={!!question.properties?.maxAnswers}
          onClick={() => setIsPopoverOpen(!isPopoverOpen)}
          value={pillValue}
        />
      </Layout.Flex>
      <OverlayPopover
        className={styles['answers-format-popover']}
        container={popoverRef.current}
        hasArrow
        isActive={isPopoverOpen}
        onHide={() => setIsPopoverOpen(false)}
        target={pillRef.current}
      >
        <>
          <Fieldset legend="Answer Format">
            <Layout.Flex.Column className={styles['answers-format-column']} gap="small">
              {[FORMATS.NUMBER, FORMATS.CURRENCY, FORMATS.PERCENT].map((format) => (
                <Radio
                  isChecked={propertiesToApply[format.value]}
                  key={format.value}
                  label={format.name}
                  onChange={() =>
                    setPropertiesToApply((prev) => ({
                      ...prev,
                      [FORMATS.CURRENCY.value]: false,
                      [FORMATS.NUMBER.value]: false,
                      [FORMATS.PERCENT.value]: false,
                      [format.value]: true,
                      ...(format.value === FORMATS.PERCENT.value && {
                        maxNumber: 100,
                        minNumber: 0,
                      }),
                    }))
                  }
                  value={format.value}
                />
              ))}
            </Layout.Flex.Column>
          </Fieldset>
          <hr />
          <Fieldset legend="Additional Options">
            <Checkbox
              isChecked={propertiesToApply.onlyWholeNum}
              label="Force whole number"
              onChange={() =>
                setPropertiesToApply((prev) => ({
                  ...prev,
                  onlyWholeNum: !prev.onlyWholeNum,
                }))
              }
              value={propertiesToApply.onlyWholeNum}
            />
          </Fieldset>
          <Button isDisabled={isApplyDisabled} onClick={handleApply} text="Apply" />
        </>
      </OverlayPopover>
    </>
  );
};

AnswersFormat.propTypes = {
  question: PropTypes.object.isRequired,
  setQuestion: PropTypes.func.isRequired,
};

export default AnswersFormat;
